import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragEndEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import CanvasToolbar, { ToolType, StickyColor, STICKY_COLORS, ShapeType } from './CanvasToolbar';
import CanvasStickyNote, { StickyNote } from './CanvasStickyNote';
import ActionItemsPanel from '../ActionItemsPanel';
import AiReportPanel from '../AiReportPanel';
import JiraPanel from '../JiraPanel';
import CelebrationEffect from './CelebrationEffect';
import type { CelebrationMode } from './CelebrationEffect';
import { useBoardStore } from '../../store/boardStore';

// Canvas Element Types
export interface CanvasEmoji {
  id: string;
  emoji: string;
  x: number;
  y: number;
  size: number;
  zIndex: number;
}

export interface CanvasText {
  id: string;
  content: string;
  x: number;
  y: number;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  color: string;
  zIndex: number;
}

export interface CanvasShape {
  id: string;
  type: ShapeType;
  x: number;
  y: number;
  width: number;
  height: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  zIndex: number;
}

export interface CanvasFrame {
  id: string;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  zIndex: number;
}

export interface CanvasConnector {
  id: string;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  color: string;
  strokeWidth: number;
  zIndex: number;
}

export interface DrawingPath {
  id: string;
  points: { x: number; y: number }[];
  color: string;
  strokeWidth: number;
  zIndex: number;
}

interface BoardListItem {
  id: string;
  title: string;
}

interface BoardColumn {
  id: string;
  title: string;
  description?: string;
  column_type: string;
  color: string;
  order: number;
}

interface CanvasBoardProps {
  boardId: string;
  boardTitle: string;
  teamId?: string;
  onBack: () => void;
  initialStickies?: StickyNote[];
  columns?: BoardColumn[];
  // Returns the real ID from backend, or null if failed
  onStickyCreate?: (sticky: StickyNote) => Promise<string | null>;
  onStickyUpdate?: (id: string, updates: Partial<StickyNote>) => void;
  onStickyDelete?: (id: string) => void;
  onStickyToggleVisibility?: (id: string) => void;
  currentUserId?: string;
  boards?: BoardListItem[];
}

// Session Storage helpers for camera position (like Ludi.co)
const getCameraKey = (boardId: string) => `retrosive/${boardId}/camera`;
const getElementsKey = (boardId: string) => `retrosive/${boardId}/elements`;

const loadCameraFromStorage = (boardId: string): { x: number; y: number; scale: number } | null => {
  try {
    const stored = sessionStorage.getItem(getCameraKey(boardId));
    if (stored) {
      const parsed = JSON.parse(stored);
      return { x: parsed.position?.x ?? 0, y: parsed.position?.y ?? 0, scale: parsed.scale ?? 1 };
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

const saveCameraToStorage = (boardId: string, x: number, y: number, scale: number) => {
  try {
    sessionStorage.setItem(getCameraKey(boardId), JSON.stringify({
      position: { x, y },
      scale
    }));
  } catch {
    // Ignore storage errors
  }
};

// LocalStorage helpers for canvas elements (persist across refresh)
interface CanvasElements {
  emojis: CanvasEmoji[];
  texts: CanvasText[];
  shapes: CanvasShape[];
  frames: CanvasFrame[];
  connectors: CanvasConnector[];
  drawings: DrawingPath[];
  columnZoneSizes?: Record<string, { width: number; height: number }>;
}

const loadElementsFromStorage = (boardId: string): CanvasElements | null => {
  try {
    const stored = localStorage.getItem(getElementsKey(boardId));
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return null;
};

const saveElementsToStorage = (boardId: string, elements: CanvasElements) => {
  try {
    localStorage.setItem(getElementsKey(boardId), JSON.stringify(elements));
  } catch {
    // Ignore storage errors
  }
};

const toSmoothPath = (points: { x: number; y: number }[]): string => {
  if (points.length < 2) return '';
  const d = points.reduce((acc, point, i, pts) => {
    if (i === 0) return `M ${point.x} ${point.y}`;
    const prev = pts[i - 1];
    const midX = (prev.x + point.x) / 2;
    const midY = (prev.y + point.y) / 2;
    return `${acc} Q ${prev.x} ${prev.y} ${midX} ${midY}`;
  }, '');
  const last = points[points.length - 1];
  return `${d} L ${last.x} ${last.y}`;
};

const CanvasBoard: React.FC<CanvasBoardProps> = ({
  boardId,
  boardTitle,
  teamId,
  onBack,
  initialStickies = [],
  columns = [],
  onStickyCreate,
  onStickyUpdate,
  onStickyDelete,
  onStickyToggleVisibility,
  currentUserId,
  boards = [],
}) => {
  const navigate = useNavigate();

  // Board dropdown state
  const [showBoardDropdown, setShowBoardDropdown] = useState(false);
  const boardDropdownRef = useRef<HTMLDivElement>(null);

  // Sidekick panel state
  const [activeSidekick, setActiveSidekick] = useState<'actions' | 'ai-report' | 'jira' | null>(null);
  const [sidekickWidth, setSidekickWidth] = useState(420);
  const isResizingRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  
  // Load initial camera from session storage
  const initialCamera = loadCameraFromStorage(boardId);
  
  // Canvas state
  const [zoom, setZoom] = useState(initialCamera?.scale ?? 1);
  const [pan, setPan] = useState({ x: initialCamera?.x ?? 0, y: initialCamera?.y ?? 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [spacePressed, setSpacePressed] = useState(false);

  // Tool state
  const [selectedTool, setSelectedTool] = useState<ToolType>('select');
  const [selectedColor, setSelectedColor] = useState<StickyColor>(STICKY_COLORS[10]); // Yellow
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Stickies state
  const [stickies, setStickies] = useState<StickyNote[]>(initialStickies);
  const [selectedStickyId, setSelectedStickyId] = useState<string | null>(null);
  const [nextZIndex, setNextZIndex] = useState(1);
  const [newlyCreatedStickyId, setNewlyCreatedStickyId] = useState<string | null>(null);

  // Load initial elements from localStorage
  const initialElements = loadElementsFromStorage(boardId);
  
  // Canvas elements state (initialized from localStorage)
  const [emojis, setEmojis] = useState<CanvasEmoji[]>(initialElements?.emojis ?? []);
  const [texts, setTexts] = useState<CanvasText[]>(initialElements?.texts ?? []);
  const [shapes, setShapes] = useState<CanvasShape[]>(initialElements?.shapes ?? []);
  const [frames, setFrames] = useState<CanvasFrame[]>(initialElements?.frames ?? []);
  const [connectors, setConnectors] = useState<CanvasConnector[]>(initialElements?.connectors ?? []);
  const [drawings, setDrawings] = useState<DrawingPath[]>(initialElements?.drawings ?? []);
  const [columnZoneSizes, setColumnZoneSizes] = useState<Record<string, { width: number; height: number }>>(
    initialElements?.columnZoneSizes ?? {}
  );
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<{ x: number; y: number }[]>([]);
  const [penColor, setPenColor] = useState('#000000');
  const [penSize, setPenSize] = useState(3);
  
  // Connector state
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectorStart, setConnectorStart] = useState<{ x: number; y: number } | null>(null);
  
  // Selected element (for any type)
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [selectedElementType, setSelectedElementType] = useState<'sticky' | 'emoji' | 'text' | 'shape' | 'frame' | null>(null);
  
  // Dragging state for canvas elements
  const [isDraggingElement, setIsDraggingElement] = useState(false);
  const [draggedElement, setDraggedElement] = useState<{ id: string; type: string; startX: number; startY: number; elementX: number; elementY: number } | null>(null);
  
  // Editing text
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  
  // Resizing frame or shape
  const [isResizingFrame, setIsResizingFrame] = useState(false);
  const [resizingFrame, setResizingFrame] = useState<{ id: string; type: 'frame' | 'shape' | 'column'; startX: number; startY: number; startWidth: number; startHeight: number; corner: string } | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Live cursors + celebration
  const { cursors, updateCursorPosition, sendWebSocketMessage } = useBoardStore();
  const cursorThrottleRef = useRef<number>(0);

  // Celebration state
  const [activeCelebration, setActiveCelebration] = useState<{ mode: CelebrationMode; triggeredBy: string; key: number } | null>(null);
  const [showCelebrationMenu, setShowCelebrationMenu] = useState(false);
  const celebrationMenuRef = useRef<HTMLDivElement>(null);
  const lastCelebrationIdRef = useRef<number>(0);

  // Subscribe directly to Zustand store — bypasses React render cycle,
  // guarantees the effect fires even when cursor_moved floods re-renders.
  useEffect(() => {
    return useBoardStore.subscribe((state) => {
      const event = state.celebrationEvent;
      if (!event) return;
      if (event.id === lastCelebrationIdRef.current) return;
      lastCelebrationIdRef.current = event.id;
      setActiveCelebration({
        mode: event.mode as CelebrationMode,
        triggeredBy: event.triggeredBy,
        key: event.id,
      });
    });
  }, []);

  // Close celebration menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (celebrationMenuRef.current && !celebrationMenuRef.current.contains(e.target as Node)) {
        setShowCelebrationMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const triggerCelebrationMode = useCallback((mode: CelebrationMode) => {
    setShowCelebrationMenu(false);
    // Trigger locally immediately
    setActiveCelebration({ mode, triggeredBy: 'Sen', key: Date.now() });
    // Broadcast to team via WebSocket
    sendWebSocketMessage({ action: 'celebrate', mode });
  }, [sendWebSocketMessage]);

  // Loading state for card creation
  const [isCreatingCard, setIsCreatingCard] = useState(false);

  // Dirty tracking - store full sticky data for reliable sync
  const dirtyStickiesRef = useRef<Map<string, { content: string; x: number; y: number }>>(new Map());
  const lastSyncRef = useRef<number>(Date.now());

  // Debounce timers for WS content broadcast (per card)
  const contentBroadcastTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  // Always-current refs for zoom/pan (used by wheel handler to avoid reattaching listener)
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panRef = useRef(pan);
  panRef.current = pan;

  // Always-current refs for canvas elements (avoids stale closure in callbacks)
  const emojisRef = useRef(emojis);
  emojisRef.current = emojis;
  const textsRef = useRef(texts);
  textsRef.current = texts;
  const shapesRef = useRef(shapes);
  shapesRef.current = shapes;
  const framesRef = useRef(frames);
  framesRef.current = frames;
  const connectorsRef = useRef(connectors);
  connectorsRef.current = connectors;
  const drawingsRef = useRef(drawings);
  drawingsRef.current = drawings;
  const columnZoneSizesRef = useRef(columnZoneSizes);
  columnZoneSizesRef.current = columnZoneSizes;

  // Refs for resize/drag state (stale-closure–safe access in handleMouseUp)
  const isResizingFrameRef = useRef(isResizingFrame);
  isResizingFrameRef.current = isResizingFrame;
  const resizingFrameRef = useRef(resizingFrame);
  resizingFrameRef.current = resizingFrame;
  const isDraggingElementRef = useRef(isDraggingElement);
  isDraggingElementRef.current = isDraggingElement;
  const draggedElementRef = useRef(draggedElement);
  draggedElementRef.current = draggedElement;

  // Throttle & debounce timers for canvas element broadcasts
  const elBroadcastThrottleRef = useRef<Record<string, number>>({});
  const elBroadcastTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const broadcastElements = useCallback((elementType: string, elements: unknown) => {
    sendWebSocketMessage({ action: 'canvas_element_update', element_type: elementType, elements });
  }, [sendWebSocketMessage]);

  const broadcastElementsThrottled = useCallback((elementType: string, elements: unknown) => {
    const now = Date.now();
    if (now - (elBroadcastThrottleRef.current[elementType] || 0) < 50) return;
    elBroadcastThrottleRef.current[elementType] = now;
    sendWebSocketMessage({ action: 'canvas_element_update', element_type: elementType, elements });
  }, [sendWebSocketMessage]);

  const broadcastElementsDebounced = useCallback((elementType: string, elements: unknown, delay = 400) => {
    if (elBroadcastTimersRef.current[elementType]) clearTimeout(elBroadcastTimersRef.current[elementType]);
    elBroadcastTimersRef.current[elementType] = setTimeout(() => {
      sendWebSocketMessage({ action: 'canvas_element_update', element_type: elementType, elements });
      delete elBroadcastTimersRef.current[elementType];
    }, delay);
  }, [sendWebSocketMessage]);

  // Sync stickies from parent (boardStore). Preserve any card that is currently
  // being edited/dragged locally (i.e. exists in dirtyStickiesRef) so incoming
  // remote updates don't overwrite in-progress work.
  useEffect(() => {
    setStickies(prevStickies => {
      return initialStickies.map(incoming => {
        if (dirtyStickiesRef.current.has(incoming.id)) {
          return prevStickies.find(s => s.id === incoming.id) || incoming;
        }
        return incoming;
      });
    });
    if (initialStickies.length > 0) {
      const maxZIndex = Math.max(...initialStickies.map(s => s.zIndex || 0));
      setNextZIndex(prev => Math.max(prev, maxZIndex + 1));
    }
  }, [initialStickies]);

  // Save camera position to session storage when it changes (like Ludi.co)
  useEffect(() => {
    // Debounce: save after panning stops
    const timeout = setTimeout(() => {
      saveCameraToStorage(boardId, pan.x, pan.y, zoom);
    }, 100);
    return () => clearTimeout(timeout);
  }, [boardId, pan, zoom]);

  // Save canvas elements to localStorage when they change
  useEffect(() => {
    const timeout = setTimeout(() => {
      saveElementsToStorage(boardId, {
        emojis,
        texts,
        shapes,
        frames,
        connectors,
        drawings,
        columnZoneSizes,
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, [boardId, emojis, texts, shapes, frames, connectors, drawings, columnZoneSizes]);

  // Listen for remote canvas element updates from other users
  useEffect(() => {
    const handler = (e: Event) => {
      const msg = (e as CustomEvent<any>).detail;
      if (msg.type === 'canvas_elements_updated') {
        switch (msg.element_type) {
          case 'emojis': setEmojis(msg.elements); break;
          case 'texts': setTexts(msg.elements); break;
          case 'shapes': setShapes(msg.elements); break;
          case 'frames': setFrames(msg.elements); break;
          case 'connectors': setConnectors(msg.elements); break;
          case 'drawings': setDrawings(msg.elements); break;
          case 'columnZoneSizes': setColumnZoneSizes(msg.elements); break;
        }
      } else if (msg.type === 'canvas_board_state') {
        const ce = msg.canvas_elements || {};
        if (ce.emojis !== undefined) setEmojis(ce.emojis);
        if (ce.texts !== undefined) setTexts(ce.texts);
        if (ce.shapes !== undefined) setShapes(ce.shapes);
        if (ce.frames !== undefined) setFrames(ce.frames);
        if (ce.connectors !== undefined) setConnectors(ce.connectors);
        if (ce.drawings !== undefined) setDrawings(ce.drawings);
        if (ce.columnZoneSizes !== undefined) setColumnZoneSizes(ce.columnZoneSizes);
      }
    };
    window.addEventListener('retrosive_canvas_ws', handler);
    return () => window.removeEventListener('retrosive_canvas_ws', handler);
  }, []);

  // Close board dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (boardDropdownRef.current && !boardDropdownRef.current.contains(e.target as Node)) {
        setShowBoardDropdown(false);
      }
    };
    
    if (showBoardDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showBoardDropdown]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Keyboard shortcuts (only essential ones - no letter shortcuts)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return;

      // Space for panning
      if (e.code === 'Space' && !spacePressed) {
        e.preventDefault();
        setSpacePressed(true);
      }

      // Delete selected element
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedStickyId) {
          e.preventDefault();
          handleDeleteSticky(selectedStickyId);
        } else if (selectedElementId && selectedElementType) {
          e.preventDefault();
          if (selectedElementType === 'frame') {
            const next = framesRef.current.filter(f => f.id !== selectedElementId);
            setFrames(next); broadcastElements('frames', next);
          } else if (selectedElementType === 'shape') {
            const next = shapesRef.current.filter(s => s.id !== selectedElementId);
            setShapes(next); broadcastElements('shapes', next);
          } else if (selectedElementType === 'text') {
            const next = textsRef.current.filter(t => t.id !== selectedElementId);
            setTexts(next); broadcastElements('texts', next);
          } else if (selectedElementType === 'emoji') {
            const next = emojisRef.current.filter(em => em.id !== selectedElementId);
            setEmojis(next); broadcastElements('emojis', next);
          }
          setSelectedElementId(null);
          setSelectedElementType(null);
        }
      }

      // Escape to deselect
      if (e.key === 'Escape') {
        setSelectedStickyId(null);
        setNewlyCreatedStickyId(null);
        setSelectedElementId(null);
        setSelectedElementType(null);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        setSpacePressed(false);
        setIsPanning(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spacePressed, selectedStickyId, selectedElementId, selectedElementType]);

  // Mouse wheel zoom - zooms to mouse position
  // Empty dep array: refs are always current, no need to reattach the listener on every zoom/pan change
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();

        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const delta = e.deltaY > 0 ? -0.02 : 0.02;
        const oldZoom = zoomRef.current;
        const newZoom = Math.max(0.25, Math.min(3, oldZoom + delta));

        if (newZoom === oldZoom) return;

        const zoomRatio = newZoom / oldZoom;
        const currentPan = panRef.current;
        const newPanX = mouseX - (mouseX - currentPan.x) * zoomRatio;
        const newPanY = mouseY - (mouseY - currentPan.y) * zoomRatio;

        setZoom(newZoom);
        setPan({ x: newPanX, y: newPanY });
      }
    };

    const canvas = canvasRef.current;
    if (canvas) {
      canvas.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (canvas) {
        canvas.removeEventListener('wheel', handleWheel);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pan handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Pan with Space, middle mouse button, OR left click in select mode on empty canvas
    if (spacePressed || e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      return;
    }
    
    // Left click pan when in select mode and clicking on canvas background
    if (e.button === 0 && selectedTool === 'select') {
      const target = e.target as HTMLElement;
      const isCanvasClick = target === canvasRef.current || 
                            target === contentRef.current ||
                            target.classList.contains('canvas-background');
      
      if (isCanvasClick) {
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      }
    }
  }, [spacePressed, pan, selectedTool]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Throttle cursor broadcasts to ~30fps
    const now = Date.now();
    if (now - cursorThrottleRef.current > 33) {
      cursorThrottleRef.current = now;
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const canvasX = (e.clientX - rect.left - pan.x) / zoom;
        const canvasY = (e.clientY - rect.top - pan.y) / zoom;
        updateCursorPosition(canvasX, canvasY);
      }
    }

    if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y,
      });
      return;
    }
    
    // Dragging canvas element (emoji, text, shape, frame)
    if (isDraggingElement && draggedElement) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const currentX = (e.clientX - rect.left - pan.x) / zoom;
      const currentY = (e.clientY - rect.top - pan.y) / zoom;
      
      const deltaX = currentX - draggedElement.startX;
      const deltaY = currentY - draggedElement.startY;
      
      const newX = draggedElement.elementX + deltaX;
      const newY = draggedElement.elementY + deltaY;
      
      switch (draggedElement.type) {
        case 'emoji': {
          const next = emojisRef.current.map(e => e.id === draggedElement.id ? { ...e, x: newX, y: newY } : e);
          setEmojis(next); broadcastElementsThrottled('emojis', next);
          break;
        }
        case 'text': {
          const next = textsRef.current.map(t => t.id === draggedElement.id ? { ...t, x: newX, y: newY } : t);
          setTexts(next); broadcastElementsThrottled('texts', next);
          break;
        }
        case 'shape': {
          const next = shapesRef.current.map(s => s.id === draggedElement.id ? { ...s, x: newX, y: newY } : s);
          setShapes(next); broadcastElementsThrottled('shapes', next);
          break;
        }
        case 'frame': {
          const next = framesRef.current.map(f => f.id === draggedElement.id ? { ...f, x: newX, y: newY } : f);
          setFrames(next); broadcastElementsThrottled('frames', next);
          break;
        }
      }
      return;
    }

    // Resizing frame or shape
    if (isResizingFrame && resizingFrame) {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const currentX = (e.clientX - rect.left - pan.x) / zoom;
      const currentY = (e.clientY - rect.top - pan.y) / zoom;

      const deltaX = currentX - resizingFrame.startX;
      const deltaY = currentY - resizingFrame.startY;
      const newWidth = Math.max(40, resizingFrame.startWidth + deltaX);
      const newHeight = Math.max(40, resizingFrame.startHeight + deltaY);

      if (resizingFrame.type === 'frame') {
        const next = framesRef.current.map(f =>
          f.id !== resizingFrame.id ? f : { ...f, width: newWidth, height: newHeight }
        );
        setFrames(next); broadcastElementsThrottled('frames', next);
      } else if (resizingFrame.type === 'shape') {
        const next = shapesRef.current.map(s =>
          s.id !== resizingFrame.id ? s : { ...s, width: newWidth, height: newHeight }
        );
        setShapes(next); broadcastElementsThrottled('shapes', next);
      } else {
        const next = { ...columnZoneSizesRef.current, [resizingFrame.id]: { width: newWidth, height: newHeight } };
        setColumnZoneSizes(next); broadcastElementsThrottled('columnZoneSizes', next);
      }
      return;
    }
    
    // Drawing with pen tool
    if (isDrawing && selectedTool === 'pen') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;
      
      setCurrentPath(prev => [...prev, { x, y }]);
    }
  }, [isPanning, panStart, isDrawing, selectedTool, pan, zoom, isDraggingElement, draggedElement, isResizingFrame, resizingFrame, updateCursorPosition, broadcastElementsThrottled]);

  const handleMouseUp = useCallback(() => {
    setIsPanning(false);

    // Broadcast final position when drag ends
    if (isDraggingElementRef.current && draggedElementRef.current) {
      switch (draggedElementRef.current.type) {
        case 'emoji': broadcastElements('emojis', emojisRef.current); break;
        case 'text':  broadcastElements('texts',  textsRef.current);  break;
        case 'shape': broadcastElements('shapes', shapesRef.current); break;
        case 'frame': broadcastElements('frames', framesRef.current); break;
      }
    }
    setIsDraggingElement(false);
    setDraggedElement(null);

    // Broadcast final size when resize ends
    if (isResizingFrameRef.current && resizingFrameRef.current) {
      const rf = resizingFrameRef.current;
      if (rf.type === 'frame')        broadcastElements('frames',           framesRef.current);
      else if (rf.type === 'shape')   broadcastElements('shapes',           shapesRef.current);
      else                            broadcastElements('columnZoneSizes',  columnZoneSizesRef.current);
    }
    setIsResizingFrame(false);
    setResizingFrame(null);

    // Finish drawing
    if (isDrawing && currentPath.length > 1) {
      const newDrawing: DrawingPath = {
        id: `drawing-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        points: currentPath,
        color: penColor,
        strokeWidth: penSize,
        zIndex: nextZIndex,
      };
      const nextDrawings = [...drawingsRef.current, newDrawing];
      setDrawings(nextDrawings);
      setNextZIndex(prev => prev + 1);
      broadcastElements('drawings', nextDrawings);
    }
    setIsDrawing(false);
    setCurrentPath([]);
  }, [isDrawing, currentPath, penColor, penSize, nextZIndex, broadcastElements]);

  // Start drawing
  const handleMouseDownForDrawing = useCallback((e: React.MouseEvent) => {
    if (selectedTool === 'pen') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX - rect.left - pan.x) / zoom;
      const y = (e.clientY - rect.top - pan.y) / zoom;
      
      setIsDrawing(true);
      setCurrentPath([{ x, y }]);
    }
  }, [selectedTool, pan, zoom]);

  // Start dragging an element
  const handleElementDragStart = useCallback((
    e: React.MouseEvent,
    id: string,
    type: 'emoji' | 'text' | 'shape' | 'frame',
    elementX: number,
    elementY: number
  ) => {
    e.stopPropagation();
    e.preventDefault();
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const startX = (e.clientX - rect.left - pan.x) / zoom;
    const startY = (e.clientY - rect.top - pan.y) / zoom;
    
    setIsDraggingElement(true);
    setDraggedElement({ id, type, startX, startY, elementX, elementY });
    setSelectedElementId(id);
    setSelectedElementType(type);
  }, [pan, zoom]);

  // Start resizing a frame, shape, or column zone
  const handleFrameResizeStart = useCallback((
    e: React.MouseEvent,
    id: string,
    type: 'frame' | 'shape' | 'column',
    width: number,
    height: number,
    corner: string
  ) => {
    e.stopPropagation();
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const startX = (e.clientX - rect.left - pan.x) / zoom;
    const startY = (e.clientY - rect.top - pan.y) / zoom;

    setIsResizingFrame(true);
    setResizingFrame({ id, type, startX, startY, startWidth: width, startHeight: height, corner });
  }, [pan, zoom]);

  // Update text content
  const handleTextUpdate = useCallback((id: string, content: string) => {
    const next = textsRef.current.map(t => t.id === id ? { ...t, content } : t);
    setTexts(next);
    broadcastElementsDebounced('texts', next);
  }, [broadcastElementsDebounced]);

  // Update frame title
  const handleFrameTitleUpdate = useCallback((id: string, title: string) => {
    const next = framesRef.current.map(f => f.id === id ? { ...f, title } : f);
    setFrames(next);
    broadcastElementsDebounced('frames', next);
  }, [broadcastElementsDebounced]);

  // Helper to get center of viewport in canvas coordinates
  const getViewportCenter = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 500, y: 500 };

    const rect = canvas.getBoundingClientRect();
    const viewportCenterX = (rect.width / 2 - pan.x) / zoom;
    const viewportCenterY = (rect.height / 2 - pan.y) / zoom;
    
    // Add some randomness to prevent stacking
    const offsetX = (Math.random() - 0.5) * 100;
    const offsetY = (Math.random() - 0.5) * 100;
    
    return { x: viewportCenterX + offsetX, y: viewportCenterY + offsetY };
  }, [pan, zoom]);

  // Add sticky note - places in center of visible viewport
  const handleAddSticky = useCallback(async (color: StickyColor) => {
    if (isCreatingCard) return; // Prevent double-clicks
    
    const center = getViewportCenter();
    const stickyWidth = 160;
    const stickyHeight = 120;

    const newSticky: StickyNote = {
      id: '', // Will be set by backend
      content: '',
      x: center.x - stickyWidth / 2,
      y: center.y - stickyHeight / 2,
      width: stickyWidth,
      height: stickyHeight,
      color,
      zIndex: nextZIndex,
    };

    // Switch back to select mode immediately
    setSelectedTool('select');
    setIsCreatingCard(true);
    
    try {
      // Wait for backend to create card and return real ID
      const realId = await onStickyCreate?.(newSticky);
      
      if (realId) {
        // Card was created - it will appear via initialStickies prop update
        setNextZIndex(prev => prev + 1);
        // Select the newly created card
        setSelectedStickyId(realId);
        setNewlyCreatedStickyId(realId);
      }
    } catch (error) {
      console.error('Failed to create sticky:', error);
    } finally {
      setIsCreatingCard(false);
    }
  }, [getViewportCenter, nextZIndex, onStickyCreate, isCreatingCard]);

  // Add emoji to canvas
  const handleAddEmoji = useCallback((emoji: string) => {
    const center = getViewportCenter();
    const newEmoji: CanvasEmoji = {
      id: `emoji-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      emoji,
      x: center.x - 24,
      y: center.y - 24,
      size: 48,
      zIndex: nextZIndex,
    };
    const next = [...emojisRef.current, newEmoji];
    setEmojis(next);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newEmoji.id);
    setSelectedElementType('emoji');
    broadcastElements('emojis', next);
  }, [getViewportCenter, nextZIndex, broadcastElements]);

  // Add text to canvas
  const handleAddText = useCallback(() => {
    const center = getViewportCenter();
    const newText: CanvasText = {
      id: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      content: 'Double-click to edit',
      x: center.x - 80,
      y: center.y - 16,
      fontSize: 18,
      fontWeight: 'normal',
      color: '#1f2937',
      zIndex: nextZIndex,
    };
    const next = [...textsRef.current, newText];
    setTexts(next);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newText.id);
    setSelectedElementType('text');
    broadcastElements('texts', next);
  }, [getViewportCenter, nextZIndex, broadcastElements]);

  // Add shape to canvas
  const handleAddShape = useCallback((shapeType: ShapeType) => {
    const center = getViewportCenter();
    const newShape: CanvasShape = {
      id: `shape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: shapeType,
      x: center.x - 50,
      y: center.y - 50,
      width: 100,
      height: shapeType === 'arrow' ? 40 : 100,
      fill: 'transparent',
      stroke: '#3b82f6',
      strokeWidth: 2,
      zIndex: nextZIndex,
    };
    const next = [...shapesRef.current, newShape];
    setShapes(next);
    setNextZIndex(prev => prev + 1);
    setSelectedElementId(newShape.id);
    setSelectedElementType('shape');
    broadcastElements('shapes', next);
  }, [getViewportCenter, nextZIndex, broadcastElements]);

  // Add frame to canvas
  const handleAddFrame = useCallback(() => {
    const center = getViewportCenter();
    const newFrame: CanvasFrame = {
      id: `frame-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title: 'Frame',
      x: center.x - 150,
      y: center.y - 100,
      width: 300,
      height: 200,
      color: '#6366f1',
      zIndex: 0,
    };
    const next = [...framesRef.current, newFrame];
    setFrames(next);
    setSelectedElementId(newFrame.id);
    setSelectedElementType('frame');
    broadcastElements('frames', next);
  }, [getViewportCenter, broadcastElements]);

  // Click on canvas to add sticky at click position
  const handleCanvasClick = useCallback(async (e: React.MouseEvent) => {
    // Allow click on canvas or content area (but not on stickies)
    const target = e.target as HTMLElement;
    const isCanvasClick = target === canvasRef.current || 
                          target === contentRef.current ||
                          target.classList.contains('canvas-background');
    
    if (!isCanvasClick) {
      // Clicked on something else (probably a sticky), just deselect
      if (selectedTool === 'select') {
        setSelectedStickyId(null);
      }
      return;
    }
    
    if (selectedTool === 'sticky') {
      if (isCreatingCard) return; // Prevent double-clicks
      
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      
      // Convert click position to canvas coordinates
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      
      // Convert screen coords to canvas coords (accounting for pan and zoom)
      const canvasX = (clickX - pan.x) / zoom;
      const canvasY = (clickY - pan.y) / zoom;

      const stickyWidth = 160;
      const stickyHeight = 120;

      const newSticky: StickyNote = {
        id: '', // Will be set by backend
        content: '',
        x: canvasX - stickyWidth / 2,  // Center sticky on click point
        y: canvasY - stickyHeight / 2,
        width: stickyWidth,
        height: stickyHeight,
        color: selectedColor,
        zIndex: nextZIndex,
      };

      // Switch back to select mode immediately
      setSelectedTool('select');
      setIsCreatingCard(true);
      
      try {
        // Wait for backend to create card and return real ID
        const realId = await onStickyCreate?.(newSticky);
        
        if (realId) {
          // Card was created - it will appear via initialStickies prop update
          setNextZIndex(prev => prev + 1);
          // Select the newly created card and auto-open edit mode
          setSelectedStickyId(realId);
          setNewlyCreatedStickyId(realId);
        }
      } catch (error) {
        console.error('Failed to create sticky:', error);
      } finally {
        setIsCreatingCard(false);
      }
    } else if (selectedTool === 'connector') {
      // Connector tool - click to set start/end points
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      const canvasX = (clickX - pan.x) / zoom;
      const canvasY = (clickY - pan.y) / zoom;

      if (!isConnecting || !connectorStart) {
        // First click - set start point
        setConnectorStart({ x: canvasX, y: canvasY });
        setIsConnecting(true);
      } else {
        // Second click - create connector
        const newConnector: CanvasConnector = {
          id: `connector-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          startX: connectorStart.x,
          startY: connectorStart.y,
          endX: canvasX,
          endY: canvasY,
          color: '#3b82f6',
          strokeWidth: 2,
          zIndex: nextZIndex,
        };
        const nextConnectors = [...connectorsRef.current, newConnector];
        setConnectors(nextConnectors);
        setNextZIndex(prev => prev + 1);
        setConnectorStart(null);
        setIsConnecting(false);
        broadcastElements('connectors', nextConnectors);
      }
    } else {
      // Deselect all
      setSelectedStickyId(null);
      setSelectedElementId(null);
      setSelectedElementType(null);
    }
  }, [selectedTool, pan, zoom, selectedColor, nextZIndex, onStickyCreate, isConnecting, connectorStart, isCreatingCard, broadcastElements]);

  // Sync dirty stickies to backend
  const syncDirtyStickies = useCallback(() => {
    const dirtyItems = Array.from(dirtyStickiesRef.current.entries());
    if (dirtyItems.length === 0) return;
    
    console.log('[Sync] Saving', dirtyItems.length, 'dirty stickies');
    
    // Sync each dirty sticky with its stored data
    dirtyItems.forEach(([id, data]) => {
      onStickyUpdate?.(id, {
        content: data.content,
        x: data.x,
        y: data.y,
      });
    });
    
    // Clear dirty map after sync
    dirtyStickiesRef.current.clear();
    lastSyncRef.current = Date.now();
  }, [onStickyUpdate]);

  // Periodic sync every 2 seconds (only if there are dirty items)
  // Frequent sync ensures data is saved before page refresh
  useEffect(() => {
    const interval = setInterval(() => {
      if (dirtyStickiesRef.current.size > 0) {
        syncDirtyStickies();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [syncDirtyStickies]);

  // Sync on visibility change (when user switches tabs, minimizes, etc.)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && dirtyStickiesRef.current.size > 0) {
        // Sync immediately when tab becomes hidden
        syncDirtyStickies();
      }
    };

    // Sync before page unload (F5, close tab, navigate away)
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (dirtyStickiesRef.current.size > 0) {
        // Trigger sync
        syncDirtyStickies();
        
        // Show browser's native "unsaved changes" dialog as fallback
        // This gives the sync a bit more time to complete
        e.preventDefault();
        e.returnValue = '';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [syncDirtyStickies]);

  // Sync when component unmounts or boardId changes (switching boards)
  useEffect(() => {
    // Capture ref value for cleanup function
    const dirtyStickies = dirtyStickiesRef.current;
    
    return () => {
      // Sync any pending changes before leaving
      if (dirtyStickies.size > 0) {
        console.log('[Sync] Unmount - saving', dirtyStickies.size, 'dirty stickies');
        // Use stored data from dirty map
        const dirtyItems = Array.from(dirtyStickies.entries());
        dirtyItems.forEach(([id, data]) => {
          onStickyUpdate?.(id, {
            content: data.content,
            x: data.x,
            y: data.y,
          });
        });
        dirtyStickies.clear();
      }
    };
  }, [boardId, onStickyUpdate]);

  // Update sticky - LOCAL ONLY for instant UX, with debounced WS broadcast
  const handleUpdateSticky = useCallback((id: string, updates: Partial<StickyNote>) => {
    setStickies(prev => {
      const newStickies = prev.map(s => s.id === id ? { ...s, ...updates } : s);
      const updatedSticky = newStickies.find(s => s.id === id);
      if (updatedSticky) {
        dirtyStickiesRef.current.set(id, {
          content: updatedSticky.content,
          x: updatedSticky.x,
          y: updatedSticky.y,
        });
      }
      return newStickies;
    });

    // Debounce content-change WS broadcast so other users see typing within ~500ms
    if (updates.content !== undefined) {
      if (contentBroadcastTimersRef.current[id]) {
        clearTimeout(contentBroadcastTimersRef.current[id]);
      }
      contentBroadcastTimersRef.current[id] = setTimeout(() => {
        const dirty = dirtyStickiesRef.current.get(id);
        if (dirty) {
          sendWebSocketMessage({ action: 'update_card', card_id: id, content: dirty.content });
        }
        delete contentBroadcastTimersRef.current[id];
      }, 500);
    }
  }, [sendWebSocketMessage]);

  // Delete sticky
  const handleDeleteSticky = useCallback((id: string) => {
    setStickies(prev => prev.filter(s => s.id !== id));
    setSelectedStickyId(null);
    onStickyDelete?.(id);
  }, [onStickyDelete]);

  // Select sticky - sync previous sticky when selection changes
  const handleSelectSticky = useCallback((id: string) => {
    // Sync previous sticky if it was dirty (user finished editing)
    if (selectedStickyId && selectedStickyId !== id) {
      const dirtyData = dirtyStickiesRef.current.get(selectedStickyId);
      if (dirtyData) {
        console.log('[Sync] Saving on selection change:', selectedStickyId);
        onStickyUpdate?.(selectedStickyId, {
          content: dirtyData.content,
          x: dirtyData.x,
          y: dirtyData.y,
        });
        dirtyStickiesRef.current.delete(selectedStickyId);
      }
    }
    
    setSelectedStickyId(id);
    // Bring to front
    setStickies(prev => 
      prev.map(s => s.id === id ? { ...s, zIndex: nextZIndex } : s)
    );
    setNextZIndex(prev => prev + 1);
  }, [nextZIndex, selectedStickyId, onStickyUpdate]);

  // Drag handlers
  const handleDragStart = (_event: DragStartEvent) => {
    // Drag started - could add visual feedback here
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;

    if (active.data.current?.type === 'sticky') {
      const stickyId = active.id as string;
      const currentSticky = stickies.find(s => s.id === stickyId);
      if (currentSticky) {
        const newX = currentSticky.x + delta.x / zoom;
        const newY = currentSticky.y + delta.y / zoom;
        handleUpdateSticky(stickyId, { x: newX, y: newY });

        // Immediately broadcast final position to all other users via WS
        sendWebSocketMessage({
          action: 'update_card',
          card_id: stickyId,
          position_x: newX,
          position_y: newY,
        });
      }
    }
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    isResizingRef.current = true;
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = sidekickWidth;
    e.preventDefault();
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!isResizingRef.current) return;
      const delta = resizeStartXRef.current - e.clientX;
      setSidekickWidth(Math.min(700, Math.max(300, resizeStartWidthRef.current + delta)));
    };
    const onMouseUp = () => { isResizingRef.current = false; };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="h-screen w-screen flex flex-col overflow-hidden bg-white">
        {/* Header */}
        <header className="h-12 bg-white border-b border-slate-200 flex items-center justify-between px-3 z-50 shrink-0 shadow-sm">
          {/* Left: Navigation */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { syncDirtyStickies(); navigate('/app/dashboard'); }}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors text-slate-500 hover:text-slate-900"
              title="Back to Dashboard"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>

            <div className="w-px h-5 bg-slate-200" />

            {/* Board Title Dropdown */}
            <div className="relative" ref={boardDropdownRef}>
              <button
                onClick={() => setShowBoardDropdown(!showBoardDropdown)}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${
                  showBoardDropdown ? 'bg-orange-50' : 'hover:bg-orange-50'
                }`}
              >
                <span className="font-semibold text-slate-900 text-sm">{boardTitle}</span>
                <svg
                  className={`w-3.5 h-3.5 text-slate-400 transition-transform ${showBoardDropdown ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {showBoardDropdown && (
                <div className="absolute top-full left-0 mt-1.5 w-60 bg-white border border-slate-100 rounded-xl shadow-lg overflow-hidden z-[100]">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Switch Board</p>
                  </div>
                  <div className="max-h-60 overflow-y-auto p-1.5">
                    {boards.length > 0 ? (
                      boards.map(board => (
                        <button
                          key={board.id}
                          onClick={() => {
                            setShowBoardDropdown(false);
                            if (board.id !== boardId) {
                              syncDirtyStickies();
                              navigate(`/app/board/${board.id}`);
                            }
                          }}
                          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors text-sm ${
                            board.id === boardId
                              ? 'bg-orange-100 text-orange-700'
                              : 'text-slate-600 hover:bg-orange-50 hover:text-slate-900'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${
                            board.id === boardId ? 'bg-orange-200' : 'bg-slate-100'
                          }`}>
                            <svg className={`w-3.5 h-3.5 ${board.id === boardId ? 'text-orange-600' : 'text-slate-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7" />
                            </svg>
                          </div>
                          <span className="font-medium truncate flex-1">{board.title}</span>
                          {board.id === boardId && (
                            <svg className="w-3.5 h-3.5 shrink-0 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))
                    ) : (
                      <p className="text-slate-400 text-sm px-3 py-4 text-center">No other boards</p>
                    )}
                  </div>
                  <div className="p-1.5 border-t border-slate-100">
                    <button
                      onClick={() => { setShowBoardDropdown(false); syncDirtyStickies(); navigate('/app/dashboard'); }}
                      className="w-full flex items-center gap-2 px-2.5 py-2 text-slate-500 hover:text-slate-900 hover:bg-orange-50 rounded-lg transition-colors text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                      All Boards
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={() => setActiveSidekick(prev => prev === 'actions' ? null : 'actions')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                activeSidekick === 'actions'
                  ? 'bg-orange-100 text-orange-700'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-orange-50'
              }`}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Actions
            </button>

            <button
              onClick={() => setActiveSidekick(prev => prev === 'ai-report' ? null : 'ai-report')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                activeSidekick === 'ai-report'
                  ? 'bg-orange-100 text-orange-700'
                  : 'text-orange-600 hover:bg-orange-100'
              }`}
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15M14.25 3.104c.251.023.501.05.75.082M19.8 15l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.607L5 14.5m14.8.5l1.402 1.402c1 1 .03 2.598-1.352 2.598H4.15c-1.38 0-2.35-1.598-1.351-2.598L4.2 14.9" />
              </svg>
              AI Report
            </button>

            {teamId && (
              <button
                onClick={() => setActiveSidekick(prev => prev === 'jira' ? null : 'jira')}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  activeSidekick === 'jira'
                    ? 'bg-orange-100 text-orange-700'
                    : 'text-orange-600 hover:bg-orange-100'
                }`}
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 32 32" fill="none">
                  <path d="M16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0z" fill="#2684FF" />
                  <path d="M23.647 8.353l-7.646 7.648-3.882-3.882-3.766 3.764 3.882 3.882 3.766 3.764 11.412-11.41-3.766-3.766z" fill="white" />
                </svg>
                Jira Items
              </button>
            )}

            <div className="w-px h-4 bg-slate-200 mx-1" />

            {/* Celebrate Button */}
            <div className="relative" ref={celebrationMenuRef}>
              <button
                onClick={() => setShowCelebrationMenu(prev => !prev)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                  showCelebrationMenu
                    ? 'bg-pink-100 text-pink-700'
                    : 'text-slate-500 hover:bg-pink-50 hover:text-pink-600'
                }`}
                title="Celebrate!"
              >
                <span className="text-sm">🎉</span>
                Celebrate
              </button>

              {showCelebrationMenu && (
                <div className="absolute top-full right-0 mt-1.5 w-52 bg-white border border-slate-100 rounded-xl shadow-xl overflow-hidden z-[200]">
                  <div className="px-3 py-2 border-b border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Choose Celebration</p>
                  </div>
                  <div className="p-1.5 flex flex-col gap-0.5">
                    {([
                      { mode: 'confetti' as CelebrationMode, icon: '🎉', label: 'Confetti' },
                      { mode: 'fireworks' as CelebrationMode, icon: '🎆', label: 'Fireworks' },
                      { mode: 'stars' as CelebrationMode, icon: '⭐', label: 'Star Shower' },
                      { mode: 'school' as CelebrationMode, icon: '🏆', label: 'Team Spirit' },
                      { mode: 'crazy' as CelebrationMode, icon: '🤪', label: 'CRAZY PARTY!' },
                    ] as const).map(({ mode, icon, label }) => (
                      <button
                        key={mode}
                        onClick={() => triggerCelebrationMode(mode)}
                        className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm text-slate-700 hover:bg-pink-50 hover:text-pink-700 transition-colors font-medium"
                      >
                        <span className="text-lg">{icon}</span>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Canvas + Sidekick area */}
        <div className="flex flex-row flex-1 overflow-hidden">

        {/* Canvas Area - Soft neutral background with subtle dot grid */}
        <div
          ref={canvasRef}
          className="flex-1 relative overflow-hidden"
          style={{
            cursor: spacePressed || isPanning ? 'grab' : selectedTool === 'sticky' ? 'crosshair' : 'default',
            backgroundColor: '#f4f4f4',
          }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={handleCanvasClick}
        >
          {/* Canvas Content */}
          <div
            ref={contentRef}
            className="canvas-background"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '5000px',
              height: '5000px',
              pointerEvents: 'auto',
            }}
            onMouseDown={handleMouseDownForDrawing}
          >
            {/* Column Zones - Template-based zones for organizing stickies */}
            {columns.length > 0 && (
              <div 
                className="absolute flex gap-6"
                style={{ 
                  left: 80, 
                  top: 80,
                  zIndex: 0,
                }}
              >
                {columns.map((column) => {
                  // Calculate zone width based on number of columns (fallback default)
                  const defaultWidth = columns.length <= 3 ? 320 : columns.length <= 4 ? 280 : 240;
                  const zoneWidth = columnZoneSizes[column.id]?.width ?? defaultWidth;
                  const zoneHeight = columnZoneSizes[column.id]?.height ?? 600;
                  
                  // Get emoji for column type
                  const getColumnEmoji = (type: string, title: string) => {
                    const titleLower = title.toLowerCase();
                    if (titleLower.includes('glad') || titleLower.includes('liked') || titleLower.includes('well')) return '😊';
                    if (titleLower.includes('mad') || titleLower.includes('angry')) return '😠';
                    if (titleLower.includes('sad')) return '😢';
                    if (titleLower.includes('start')) return '🚀';
                    if (titleLower.includes('stop')) return '🛑';
                    if (titleLower.includes('continue') || titleLower.includes('keep')) return '🔄';
                    if (titleLower.includes('learned') || titleLower.includes('learn')) return '📚';
                    if (titleLower.includes('lacked') || titleLower.includes('lack')) return '❌';
                    if (titleLower.includes('longed') || titleLower.includes('wish')) return '💭';
                    if (titleLower.includes('action')) return '📋';
                    if (titleLower.includes('improve')) return '📈';
                    if (titleLower.includes('wind') || titleLower.includes('help')) return '💨';
                    if (titleLower.includes('anchor') || titleLower.includes('hold')) return '⚓';
                    if (titleLower.includes('rock') || titleLower.includes('risk')) return '🪨';
                    if (titleLower.includes('island') || titleLower.includes('goal')) return '🏝️';
                    if (type === 'positive') return '✅';
                    if (type === 'negative') return '⚠️';
                    if (type === 'action') return '📋';
                    return '📝';
                  };
                  
                  // Get placeholder text based on column title
                  const getPlaceholderText = (title: string) => {
                    const titleLower = title.toLowerCase();
                    if (titleLower.includes('glad')) return 'What are you glad about?';
                    if (titleLower.includes('mad')) return 'What are you mad about?';
                    if (titleLower.includes('sad')) return 'What are you sad about?';
                    if (titleLower.includes('start')) return 'What should we start doing?';
                    if (titleLower.includes('stop')) return 'What should we stop doing?';
                    if (titleLower.includes('continue')) return 'What should we continue?';
                    if (titleLower.includes('liked')) return 'What did you like?';
                    if (titleLower.includes('learned')) return 'What did you learn?';
                    if (titleLower.includes('lacked')) return 'What was lacking?';
                    if (titleLower.includes('longed')) return 'What do you wish for?';
                    return `Add notes to ${title}`;
                  };
                  
                  const isColumnSelected = selectedElementId === column.id;

                  return (
                    <div
                      key={column.id}
                      className={`relative rounded-2xl border-2 border-dashed ${isEditMode && isColumnSelected ? 'ring-2 ring-orange-500' : ''}`}
                      style={{
                        width: zoneWidth,
                        height: zoneHeight,
                        borderColor: `${column.color}40`,
                        backgroundColor: `${column.color}08`,
                        cursor: isEditMode ? 'pointer' : 'default',
                      }}
                      onMouseDown={isEditMode ? (e) => {
                        e.stopPropagation();
                        setSelectedElementId(column.id);
                        setSelectedElementType('frame');
                      } : undefined}
                    >
                      {/* Column Header */}
                      <div
                        className="absolute -top-1 left-4 right-4 flex items-center gap-2 px-3 py-2 rounded-lg"
                        style={{
                          backgroundColor: `${column.color}15`,
                          transform: 'translateY(-50%)',
                        }}
                      >
                        <span className="text-lg">{getColumnEmoji(column.column_type, column.title)}</span>
                        <span
                          className="font-semibold text-sm"
                          style={{ color: column.color }}
                        >
                          {column.title}
                        </span>
                      </div>

                      {/* Placeholder text */}
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <p
                          className="text-sm opacity-30 text-center px-4"
                          style={{ color: column.color }}
                        >
                          {getPlaceholderText(column.title)}
                        </p>
                      </div>

                      {/* Resize handle — only in edit mode when selected */}
                      {isEditMode && isColumnSelected && (
                        <div
                          className="absolute -bottom-2 -right-2 w-4 h-4 bg-orange-500 rounded-full cursor-se-resize border-2 border-white shadow-lg z-10"
                          onMouseDown={(e) => handleFrameResizeStart(e, column.id, 'column', zoneWidth, zoneHeight, 'se')}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Frames (behind everything) */}
            {frames.map(frame => (
              <div
                key={frame.id}
                className={`absolute rounded-lg border-2 border-dashed cursor-move ${
                  selectedElementId === frame.id ? 'ring-2 ring-orange-500' : ''
                }`}
                style={{
                  left: frame.x,
                  top: frame.y,
                  width: frame.width,
                  height: frame.height,
                  borderColor: frame.color,
                  backgroundColor: `${frame.color}10`,
                  zIndex: frame.zIndex,
                }}
                onMouseDown={(e) => handleElementDragStart(e, frame.id, 'frame', frame.x, frame.y)}
              >
                {/* Frame title - editable */}
                <input
                  type="text"
                  value={frame.title}
                  onChange={(e) => handleFrameTitleUpdate(frame.id, e.target.value)}
                  className="absolute -top-7 left-0 text-xs font-medium px-2 py-1 rounded bg-transparent border-none outline-none"
                  style={{ color: frame.color, backgroundColor: `${frame.color}20` }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                
                {/* Resize handle (bottom-right) - only in edit mode */}
                {isEditMode && selectedElementId === frame.id && (
                  <div
                    className="absolute -bottom-2 -right-2 w-4 h-4 bg-orange-500 rounded-full cursor-se-resize border-2 border-white shadow-lg"
                    onMouseDown={(e) => handleFrameResizeStart(e, frame.id, 'frame', frame.width, frame.height, 'se')}
                  />
                )}
              </div>
            ))}

            {/* Drawings (SVG paths) */}
            <svg 
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
              style={{ zIndex: 1 }}
            >
              {drawings.map(drawing => (
                <path
                  key={drawing.id}
                  d={toSmoothPath(drawing.points)}
                  stroke={drawing.color}
                  strokeWidth={drawing.strokeWidth}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
              {/* Current drawing path */}
              {currentPath.length > 1 && (
                <path
                  d={toSmoothPath(currentPath)}
                  stroke={penColor}
                  strokeWidth={penSize}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}
              {/* Connectors */}
              {connectors.map(connector => (
                <g key={connector.id}>
                  <line
                    x1={connector.startX}
                    y1={connector.startY}
                    x2={connector.endX}
                    y2={connector.endY}
                    stroke={connector.color}
                    strokeWidth={connector.strokeWidth}
                    markerEnd="url(#arrowhead)"
                  />
                </g>
              ))}
              {/* Connector in progress */}
              {isConnecting && connectorStart && (
                <circle
                  cx={connectorStart.x}
                  cy={connectorStart.y}
                  r="6"
                  fill="#3b82f6"
                  stroke="white"
                  strokeWidth="2"
                />
              )}
              {/* Arrow marker definition */}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
                </marker>
              </defs>
            </svg>

            {/* Shapes */}
            {shapes.map(shape => (
              <div
                key={shape.id}
                className={`absolute cursor-move ${
                  selectedElementId === shape.id ? 'ring-2 ring-orange-500 ring-offset-2' : ''
                }`}
                style={{
                  left: shape.x,
                  top: shape.y,
                  width: shape.width,
                  height: shape.height,
                  zIndex: shape.zIndex,
                }}
                onMouseDown={(e) => handleElementDragStart(e, shape.id, 'shape', shape.x, shape.y)}
              >
                {isEditMode && selectedElementId === shape.id && (
                  <div
                    className="absolute -bottom-2 -right-2 w-4 h-4 bg-orange-500 rounded-full cursor-se-resize border-2 border-white shadow-lg z-10"
                    onMouseDown={(e) => handleFrameResizeStart(e, shape.id, 'shape', shape.width, shape.height, 'se')}
                  />
                )}
                <svg width="100%" height="100%" viewBox={`0 0 ${shape.width} ${shape.height}`}>
                  {shape.type === 'rectangle' && (
                    <rect
                      x="1"
                      y="1"
                      width={shape.width - 2}
                      height={shape.height - 2}
                      fill={shape.fill}
                      stroke={shape.stroke}
                      strokeWidth={shape.strokeWidth}
                      rx="4"
                    />
                  )}
                  {shape.type === 'circle' && (
                    <ellipse
                      cx={shape.width / 2}
                      cy={shape.height / 2}
                      rx={shape.width / 2 - 2}
                      ry={shape.height / 2 - 2}
                      fill={shape.fill}
                      stroke={shape.stroke}
                      strokeWidth={shape.strokeWidth}
                    />
                  )}
                  {shape.type === 'triangle' && (
                    <polygon
                      points={`${shape.width/2},2 2,${shape.height-2} ${shape.width-2},${shape.height-2}`}
                      fill={shape.fill}
                      stroke={shape.stroke}
                      strokeWidth={shape.strokeWidth}
                    />
                  )}
                  {shape.type === 'diamond' && (
                    <polygon
                      points={`${shape.width/2},2 ${shape.width-2},${shape.height/2} ${shape.width/2},${shape.height-2} 2,${shape.height/2}`}
                      fill={shape.fill}
                      stroke={shape.stroke}
                      strokeWidth={shape.strokeWidth}
                    />
                  )}
                  {shape.type === 'arrow' && (
                    <>
                      <line
                        x1="4"
                        y1={shape.height / 2}
                        x2={shape.width - 12}
                        y2={shape.height / 2}
                        stroke={shape.stroke}
                        strokeWidth={shape.strokeWidth}
                      />
                      <polygon
                        points={`${shape.width-2},${shape.height/2} ${shape.width-14},${shape.height/2-8} ${shape.width-14},${shape.height/2+8}`}
                        fill={shape.stroke}
                      />
                    </>
                  )}
                </svg>
              </div>
            ))}

            {/* Emojis */}
            {emojis.map(emojiItem => (
              <div
                key={emojiItem.id}
                className={`absolute cursor-move select-none ${
                  selectedElementId === emojiItem.id ? 'ring-2 ring-orange-500 rounded-lg ring-offset-2' : ''
                }`}
                style={{
                  left: emojiItem.x,
                  top: emojiItem.y,
                  fontSize: emojiItem.size,
                  zIndex: emojiItem.zIndex,
                  lineHeight: 1,
                }}
                onMouseDown={(e) => handleElementDragStart(e, emojiItem.id, 'emoji', emojiItem.x, emojiItem.y)}
              >
                {emojiItem.emoji}
              </div>
            ))}

            {/* Text Elements */}
            {texts.map(text => (
              <div
                key={text.id}
                className={`absolute ${editingTextId === text.id ? '' : 'cursor-move'} ${
                  selectedElementId === text.id ? 'ring-2 ring-orange-500 rounded ring-offset-2' : ''
                }`}
                style={{
                  left: text.x,
                  top: text.y,
                  fontSize: text.fontSize,
                  fontWeight: text.fontWeight,
                  color: text.color,
                  zIndex: text.zIndex,
                  minWidth: '100px',
                }}
                onMouseDown={(e) => {
                  if (editingTextId !== text.id) {
                    handleElementDragStart(e, text.id, 'text', text.x, text.y);
                  }
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  setEditingTextId(text.id);
                }}
              >
                {editingTextId === text.id ? (
                  <input
                    type="text"
                    value={text.content}
                    onChange={(e) => handleTextUpdate(text.id, e.target.value)}
                    onBlur={() => setEditingTextId(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === 'Escape') {
                        setEditingTextId(null);
                      }
                    }}
                    className="bg-white border border-orange-500 rounded px-2 py-1 outline-none"
                    style={{ 
                      fontSize: text.fontSize, 
                      fontWeight: text.fontWeight,
                      color: text.color,
                      minWidth: '100px',
                    }}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="px-2 py-1">{text.content}</span>
                )}
              </div>
            ))}

            {/* Stickies */}
            {stickies.map(sticky => (
              <CanvasStickyNote
                key={sticky.id}
                sticky={sticky}
                isSelected={selectedStickyId === sticky.id}
                onSelect={handleSelectSticky}
                onUpdate={handleUpdateSticky}
                onDelete={handleDeleteSticky}
                onToggleVisibility={onStickyToggleVisibility}
                currentUserId={currentUserId}
                zoom={zoom}
                autoEdit={newlyCreatedStickyId === sticky.id}
                onEditEnd={() => {
                  if (newlyCreatedStickyId === sticky.id) {
                    setNewlyCreatedStickyId(null);
                  }
                }}
              />
            ))}

            {/* Remote user cursors */}
            {Array.from(cursors.entries()).map(([userId, cursor]) => (
              <div
                key={userId}
                className="absolute pointer-events-none"
                style={{
                  left: cursor.x,
                  top: cursor.y,
                  zIndex: 9999,
                  transform: 'translate(0, 0)',
                }}
              >
                {/* Cursor arrow */}
                <svg
                  width="20"
                  height="24"
                  viewBox="0 0 20 24"
                  fill="none"
                  style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }}
                >
                  <path
                    d="M3 2L17 12L10 13.5L6.5 21L3 2Z"
                    fill={cursor.user.avatar_color || '#6366f1'}
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>

                {/* Avatar + Name badge */}
                <div
                  className="absolute flex items-center gap-1.5 rounded-full pl-0.5 pr-2.5 py-0.5 shadow-lg"
                  style={{
                    top: 18,
                    left: 12,
                    backgroundColor: cursor.user.avatar_color || '#6366f1',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {cursor.user.avatar_image_url ? (
                    <img
                      src={cursor.user.avatar_image_url}
                      alt={cursor.user.display_name}
                      className="w-5 h-5 rounded-full object-cover border border-white/30 shrink-0"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-white/20 border border-white/30 flex items-center justify-center shrink-0">
                      <span className="text-white font-bold leading-none" style={{ fontSize: 9 }}>
                        {cursor.user.initials || cursor.user.display_name?.substring(0, 2).toUpperCase() || '?'}
                      </span>
                    </div>
                  )}
                  <span className="text-white font-semibold" style={{ fontSize: 11 }}>
                    {cursor.user.display_name}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Toolbar */}
          <CanvasToolbar
            selectedTool={selectedTool}
            onToolSelect={setSelectedTool}
            selectedColor={selectedColor}
            onColorSelect={setSelectedColor}
            onAddSticky={handleAddSticky}
            onAddEmoji={handleAddEmoji}
            onAddText={handleAddText}
            onAddShape={handleAddShape}
            onAddFrame={handleAddFrame}
            penColor={penColor}
            onPenColorChange={setPenColor}
            penSize={penSize}
            onPenSizeChange={setPenSize}
          />

          {/* Edit Mode Toggle */}
          <div className="absolute bottom-4 right-52 flex items-center">
            <button
              onClick={() => {
                setIsEditMode(prev => !prev);
                setSelectedElementId(null);
                setSelectedElementType(null);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl shadow-md border text-xs font-medium transition-colors ${
                isEditMode
                  ? 'bg-orange-500 border-orange-500 text-white hover:bg-orange-600'
                  : 'bg-white border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }`}
              title={isEditMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
            >
              {isEditMode ? (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              )}
              {isEditMode ? 'Edit Mode' : 'Edit Mode'}
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="absolute bottom-4 right-4 flex items-center gap-0.5 bg-white rounded-xl shadow-md border border-slate-200 px-1.5 py-1">
            <button
              onClick={() => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                const newZoom = Math.min(1, canvas.clientWidth / 2000);
                setZoom(newZoom);
                setPan({ x: 0, y: 0 });
              }}
              className="p-1.5 hover:bg-orange-50 rounded-lg transition-colors text-slate-500 hover:text-slate-900"
              title="Fit to Screen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>

            <div className="w-px h-4 bg-slate-200" />

            <button
              onClick={() => setZoom(prev => Math.max(0.25, prev - 0.1))}
              className="p-1.5 hover:bg-orange-50 rounded-lg transition-colors text-slate-500 hover:text-slate-900 font-medium text-sm w-7 h-7 flex items-center justify-center"
              title="Zoom Out"
            >
              −
            </button>

            <span className="px-2 text-xs font-mono text-slate-600 min-w-[44px] text-center tabular-nums">
              {Math.round(zoom * 100)}%
            </span>

            <button
              onClick={() => setZoom(prev => Math.min(3, prev + 0.1))}
              className="p-1.5 hover:bg-orange-50 rounded-lg transition-colors text-slate-500 hover:text-slate-900 font-medium text-sm w-7 h-7 flex items-center justify-center"
              title="Zoom In"
            >
              +
            </button>
          </div>

          {spacePressed && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white text-slate-700 px-4 py-2 rounded-lg shadow-md border border-slate-200 text-xs font-medium">
              Pan mode — drag to move
            </div>
          )}

        </div>

        {/* Celebration overlay — rendered outside canvas so it covers full screen */}
        {activeCelebration && (
          <CelebrationEffect
            key={activeCelebration.key}
            mode={activeCelebration.mode}
            triggeredBy={activeCelebration.triggeredBy}
            onDone={() => setActiveCelebration(null)}
          />
        )}

          {/* Sidekick panel */}
          {activeSidekick && (
            <>
              <div
                className="w-1 cursor-col-resize bg-slate-200 hover:bg-orange-400 transition-colors shrink-0"
                onMouseDown={handleResizeMouseDown}
              />
              <div className="flex flex-col bg-white shrink-0 overflow-hidden" style={{ width: sidekickWidth }}>
                {activeSidekick === 'actions' && (
                  <ActionItemsPanel inline isOpen onClose={() => setActiveSidekick(null)} boardId={boardId} participants={[]} />
                )}
                {activeSidekick === 'ai-report' && (
                  <AiReportPanel inline isOpen onClose={() => setActiveSidekick(null)} boardId={boardId} cardCount={stickies.filter((s) => s.content.trim().length > 0).length} />
                )}
                {activeSidekick === 'jira' && (
                  <JiraPanel inline isOpen onClose={() => setActiveSidekick(null)} teamId={teamId} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </DndContext>
  );
};

export default CanvasBoard;

